import { useAllNews, useRefreshNews } from '@/hooks/useNews';
import React, { useEffect, useState } from 'react';

interface HeaderProps {
  onToggleSidebar: () => void;
  isSidebarVisible: boolean;
  currentPage?: number;
  totalPages?: number;
  mode?: 'live' | 'search' | 'popular';
  onModeChange?: (mode: 'live' | 'search' | 'popular') => void;
}

const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  isSidebarVisible,
  currentPage = 1,
  totalPages = 1,
  mode = 'live',
  onModeChange
}) => {
  const { mutate: refreshNews, isPending } = useRefreshNews();
  const { dataUpdatedAt } = useAllNews(1);
  const [updatedAgo, setUpdatedAgo] = useState<string>('');

  useEffect(() => {
    const tick = () => {
      if (!dataUpdatedAt) return;
      const secs = Math.floor((Date.now() - dataUpdatedAt) / 1000);
      if (secs < 10) setUpdatedAgo('just now');
      else if (secs < 60) setUpdatedAgo(`${secs}s ago`);
      else setUpdatedAgo(`${Math.floor(secs / 60)}m ago`);
    };
    tick();
    const id = window.setInterval(tick, 5000);
    return () => window.clearInterval(id);
  }, [dataUpdatedAt]);

  return (
    <header className="terminal-header">
      <div className="flex justify-between items-center px-3 py-1 text-xs border-b border-[#333]">
        <div className="flex items-center space-x-2">
          <button
            className="terminal-action-button bg-[#E41A1C] text-white font-bold px-2 py-0.5 text-xs"
            onClick={() => refreshNews()}
            disabled={isPending}
            aria-busy={isPending}
            aria-label={isPending ? 'Refreshing news, please wait' : 'Refresh news'}
          >
            {isPending ? 'LOADING...' : 'REFRESH'}
          </button>
          <div role="tablist" aria-label="News mode" className="news-mode-tabs">
            {([['live', 'LIVE NEWS'], ['search', 'SEARCH'], ['popular', 'NYT POPULAR']] as const).map(([value, label]) => (
              <button key={value} type="button" role="tab" aria-selected={mode === value} className={mode === value ? 'is-active' : ''} onClick={() => onModeChange?.(value)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center space-x-6 text-xs">
          <span className="text-[#666]">{updatedAgo ? `UPD ${updatedAgo}` : ''}</span>
          <span className="text-[#666]">PG {currentPage}/{totalPages}</span>
          <span className="text-terminal font-semibold tracking-wide">NEWS MONITOR</span>
        </div>
      </div>
      <div className="flex justify-between items-center px-3 py-1 bg-[#0a0a0a] text-xs border-b border-terminal">
        <div className="flex items-center space-x-3">
          <h1 className="text-white font-bold tracking-wide text-xs">TYLEE_SMART_MARKETS_TERMINAL_NEWS</h1>
          <button
            className="text-terminal hover:text-white text-base"
            onClick={onToggleSidebar}
            aria-label={isSidebarVisible ? "Hide Sidebar" : "Show Sidebar"}
          >
            {isSidebarVisible ? "×" : "☰"}
          </button>
        </div>
        <div className="flex items-center space-x-3 text-xs">
          <span className="text-[#666]">Filter Settings</span>
        </div>
      </div>
    </header>
  );
};

export default Header;
